import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';

interface RiskProfileRow {
  id: string;
  sourceApp: string;
  sourceReferenceId: string;
  submittedAt: string;
  riskScore: number | null;
  toleranceProfile: string | null;
  capacityProfile: string | null;
  capacityBinding: boolean;
  calculatedProfile: string | null;
  selectedProfile: string | null;
  profileChanged: boolean;
  payload: {
    selfEstimate?: { sentence?: string; alignment?: string };
    consistencyWarnings?: string[];
    profileChangeReason?: string;
    capacityBindingQuestions?: number[];
    preferences?: { avoid?: string; interested?: string };
  };
  pdfUrl?: string | null;
  status: string;
  dedupeOutcome?: string | null;
  matchCandidateId?: string | null;
}

const OUTCOME_LABEL: Record<string, { label: string; cls: string }> = {
  exact_match: { label: 'Attached to existing client', cls: 'bg-forest/20 text-forest' },
  fuzzy_match: { label: 'Attached to existing client (matched)', cls: 'bg-forest/20 text-forest' },
  fuzzy_queued: { label: 'New client created — duplicate queued for review', cls: 'bg-caution/20 text-caution' },
  no_match: { label: 'New client created', cls: 'bg-rust/10 text-rust' },
};

export function RiskProfilesTab({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['risk-profiles', clientId],
    queryFn: () => api<{ data: RiskProfileRow[] }>(`/api/intake/risk-profiles/by-client/${clientId}`),
  });

  if (isLoading) return <QASkeleton variant="cards" rows={2} />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load risk profiles</div>;

  if (data.data.length === 0) {
    return (
      <QAEmpty eyebrow="No risk profiles yet" title="Nothing submitted yet">
        When this client completes a questionnaire via Risk-Profile.AI, it'll land here automatically.
      </QAEmpty>
    );
  }

  return (
    <div className="space-y-4">
      {data.data.map((rp) => (
        <RiskProfileCard key={rp.id} rp={rp} />
      ))}
    </div>
  );
}

function RiskProfileCard({ rp }: { rp: RiskProfileRow }) {
  const outcome = rp.dedupeOutcome ? OUTCOME_LABEL[rp.dedupeOutcome] : null;
  const selfEstimate = rp.payload?.selfEstimate?.sentence;

  return (
    <div className="qa-card">
      <div className="flex items-center justify-between px-4 py-2 border-b border-hairline-paper text-xs qa-mono uppercase">
        <div className="flex items-center gap-3">
          <span className="text-ink-mute">submitted</span>
          <span className="text-ink">{formatDateAU(rp.submittedAt)}</span>
          <span className="text-ink-mute">·</span>
          <span className="text-ink-mute">via</span>
          <span className="text-ink">{rp.sourceApp}</span>
        </div>
        {outcome && <span className={`px-2 py-0.5 ${outcome.cls}`}>{outcome.label}</span>}
      </div>

      {rp.dedupeOutcome === 'fuzzy_queued' && rp.matchCandidateId && (
        <Link to={`/admin/match-candidates?focus=${rp.matchCandidateId}`}
          className="block px-4 py-2 bg-caution/10 border-b border-hairline-paper text-xs text-caution hover:bg-caution/20">
          A possible duplicate client was queued from this submission — review the match →
        </Link>
      )}

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {rp.selectedProfile && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs block">Agreed risk profile</span>
            <span className="text-ink text-lg">{rp.selectedProfile}</span>
            {rp.profileChanged && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-brass/20 text-brass-deep">client-adjusted</span>
            )}
          </div>
        )}
        {rp.riskScore != null && <Stat label="Tolerance score" value={String(rp.riskScore)} />}
        {rp.toleranceProfile && <Stat label="Tolerance profile" value={rp.toleranceProfile} />}
        {rp.capacityProfile && <Stat label="Capacity profile" value={rp.capacityProfile} />}
        {rp.calculatedProfile && <Stat label="Calculated profile" value={rp.calculatedProfile} />}
        {rp.capacityBinding && (
          <div className="md:col-span-2 text-xs text-caution">
            Capacity bound the profile — the calculated band is lower than tolerance alone would suggest.
          </div>
        )}
        {selfEstimate && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs">Self-estimate</span>
            <p className="text-ink mt-1 italic">{selfEstimate}</p>
          </div>
        )}
        {rp.payload?.profileChangeReason && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs">Client's reason for adjusting</span>
            <p className="text-ink mt-1 italic">"{rp.payload.profileChangeReason}"</p>
          </div>
        )}
        {Array.isArray(rp.payload?.consistencyWarnings) && rp.payload.consistencyWarnings.length > 0 && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs text-caution">Consistency warnings</span>
            <ul className="text-ink mt-1 list-disc list-inside">
              {rp.payload.consistencyWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="border-t border-hairline-paper px-4 py-2 flex items-center justify-between text-xs">
        <span className="text-ink-mute qa-mono">ref: {rp.sourceReferenceId}</span>
        {rp.pdfUrl ? (
          <a href={rp.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            ↓ View PDF
          </a>
        ) : (
          <span className="text-ink-mute italic">PDF not attached</span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="qa-eyebrow text-xs block">{label}</span>
      <span className="text-ink qa-mono">{value}</span>
    </div>
  );
}
